import React, { useState, useEffect } from 'react';
import './approvedSites.css';
import EditTzModal from './EditTzModal';
import EditSIModal from './EditSIModal';
import EditLATModal from './EditLATModal';
import EditLNGModal from './EditLNGModal';
import EditSiteModal from './EditSiteModal';
import { url } from '../../../api/endpointURL';
import { canAccess, webURL } from '../../../config';
import Input from '../../../generic_components/Input/Input';
import Select from '../../../generic_components/Select/Select';

// Import generic components
import Table from '../../../generic_components/Table/Table';
import Modal from '../../../generic_components/Modal/Modal';
import Footer from '../../../generic_components/Footer/Footer';

const ApprovedSites = () => {
  const [tableData, setTableData] = useState([]);
  const [uniqueBusinesses, setUniqueBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState('All');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [show, setShow] = useState(false);
  const [showSI, setShowSI] = useState(false);
  const [showLAT, setShowLAT] = useState(false);
  const [showLNG, setShowLNG] = useState(false);
  const [showSite, setShowSite] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [searchItem, setSearchItem] = useState('');
  const [loading, setLoading] = useState(true);

  const handleClose = () => setShow(false);
  const handleCloseSI = () => setShowSI(false);
  const handleCloseLAT = () => setShowLAT(false);
  const handleCloseLNG = () => setShowLNG(false);
  const handleCloseSite = () => setShowSite(false);

  const getRoleForBusiness = (business) => {
    let rbac_role = localStorage.getItem('rbac_role')?.toLowerCase();
    let userRole = "";
    if (rbac_role === 'tla') {
      return 'tla';
    }
    const rbac_data = JSON.parse(localStorage.getItem('rbac_data')) || [];
    if (rbac_data) {
      const roleObj = rbac_data.find(business_role => business_role.business.toLowerCase() === business.toLowerCase());
      userRole = roleObj ? roleObj.role : null
    }
    return userRole?.toLowerCase(); 
  }

  // Filter by both business and search term
  const filteredTableData = tableData?.filter((item) => {
    const matchesSearch = item.site?.toLowerCase().includes(searchItem.toLowerCase());
    const matchesBusiness = selectedBusiness === 'All' || item.business === selectedBusiness;
    return matchesSearch && matchesBusiness;
  });

  const handleShow = (row) => {
    setShow(true);
    setSelectedRow(row);
  }

  const handleSiteShow = (row) => {
    setShowSite(true);  
    setSelectedRow(row);
  }

  const handleSIShow = (row) => {
    setShowSI(true);
    setSelectedRow(row);
  }

  const handleLATShow = (row) => {
    setShowLAT(true);
    setSelectedRow(row);
  }

  const handleLNGShow = (row) => {
    setShowLNG(true);
    setSelectedRow(row);
  }

  const handleSearchItem = (e) => {
    setSearchItem(e.target.value);
  }

  const handleBusinessChange = (e) => {
    setSelectedBusiness(e.target.value);
    setSearchItem(''); // Clear search when changing business
  }

  // Prepare options for Select component
  const businessOptions = uniqueBusinesses.length > 1 
    ? [
        { value: 'All', label: 'All Businesses' },
        ...uniqueBusinesses.map(business => ({
          value: business,
          label: business
        }))
      ]
    : [];
  
  const fetchSiteCheck = async() => {
    try {
      setLoading(true);
      
      const userEmail = localStorage.getItem('email');
      const userRole = localStorage.getItem('rbac_role');

      console.log("Fetching site relationship data for user:", userEmail, "with role:", userRole);
      
      if (!userEmail || !userRole) {
        throw new Error('User authentication required');
      }
      
      const queryParams = new URLSearchParams({
        email: userEmail,
        role: userRole
      });
      
      const res = await fetch(`${url}/site-relationship?${queryParams}`);
      
      if (!res.ok) {
        throw new Error('Internal Server Error');
      }
      
      const data = await res.json();
      
      if (data.success === false) {
        throw new Error(data.message || 'Failed to fetch site data');
      }
      
      const siteData = data.data || data;
      
      // Sort by 'dt' descending
      const sortedData = siteData.sort((a, b) => new Date(b.dt) - new Date(a.dt));

      // Store all data
      setTableData(sortedData);
      
      // Extract unique businesses
      const businesses = [...new Set(sortedData.map(item => item.business))].sort();
      setUniqueBusinesses(businesses);
      
      // Set default selection
      if (businesses.length === 1) {
        setSelectedBusiness(businesses[0]);
      } else {
        setSelectedBusiness('All');
      }
      
      setSortConfig({ key: 'dt', direction: false });
      setLoading(false);
      
    } catch(error) {
      console.log(`Error in fetching site relationship data: ${error}`);
      setLoading(false);
      setTableData([]);
      setUniqueBusinesses([]);
    }
  };

  useEffect(() => {
    fetchSiteCheck();      
  }, []);

  const sortTable = (column) => {
    const direction = sortConfig.key === column ? !sortConfig.direction : true;
    const sortedData = [...tableData].sort((a, b) => {
      if (a[column] < b[column]) return direction ? -1 : 1;
      if (a[column] > b[column]) return direction ? 1 : -1;
      return 0;
    });

    setTableData(sortedData);
    setSortConfig({ key: column, direction });
  };

  const getSortIcon = (column) => {
    if (sortConfig.key === column) {
      return sortConfig.direction ? '▼' : '▲';
    }
    return '⇅';
  };

  const handleClearSearch = () => {
    setSearchItem('');
  };

  const fetchAndStoreRBAC = async (email) => {
    try {
      const url_path = url+'/fetch_rbac';
      const response = await fetch(url_path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user: email })
      });
      
      const rbacResponse = await response.json();
      localStorage.setItem('rbac_role', rbacResponse.global_role);
      localStorage.setItem('rbac_data', JSON.stringify(rbacResponse.business_roles));
      return rbacResponse;
    } catch (error) {
      console.error('Error fetching RBAC data:', error);
    }
  };
  
  useEffect(() => {
    const email = localStorage.getItem('email');
    
    if (!email) {
      return;
    }
    
    const initializeData = async () => {
      try {
        await fetchAndStoreRBAC(email);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    
    initializeData();
  }, []);

  const sites_table_columns = [
    { key: 'dt', label: 'Approved_Date', onClick:() => sortTable('dt'), icon: getSortIcon('dt') },
    { key: 'site', label: 'Site Name', onClick: () => sortTable('site'), icon: getSortIcon('site') },
    { key: 'si', label: 'SI Name', onClick:() => sortTable('si'), icon: getSortIcon('si') },
    { key: 'local_timezone', label: 'Local Timezone', onClick: () => sortTable('local_timezone'), icon: getSortIcon('local_timezone') },
    { key: 'latitude', label: 'Latitude', onClick: () => sortTable('latitude'), icon: getSortIcon('latitude') },
    { key: 'longitude', label: 'Longitude', onClick: () => sortTable('longitude'), icon: getSortIcon('longitude') },
  ];

  const sites_table_data = filteredTableData?.map((row, index) =>  (
    {
      dt: new Date(row.dt).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      business: row.business,
      site: (
        <>
          {row.site}
          <button onClick={() => handleSiteShow(row)} className='btn-tz' disabled={!canAccess("edit-site", getRoleForBusiness(row.business))}>
            &#9998;
          </button>
        </>
      ),
      si: (
        <>
          {row.si}
          <button onClick={() => handleSIShow(row)} className='btn-tz' disabled={!canAccess("edit-site", getRoleForBusiness(row.business))}>
            &#9998;
          </button>
        </>
      ),
      local_timezone: (
        <>
          {row.local_timezone}
          <button onClick={() => handleShow(row)} className='btn-tz' disabled={!canAccess("edit-site", getRoleForBusiness(row.business))}>
            &#9998;
          </button>
        </>
      ),
      latitude: (
        <>
          {row.latitude}
          <button onClick={() => handleLATShow(row)} className='btn-tz' disabled={!canAccess("edit-site", getRoleForBusiness(row.business))}>
            &#9998;
          </button>
        </>
      ),
      longitude: (
        <>
          {row.longitude}
          <button onClick={() => handleLNGShow(row)} className='btn-tz' disabled={!canAccess("edit-site", getRoleForBusiness(row.business))}>
            &#9998;
          </button>
        </>
      ),
    }
  ));

  return (
    <>
      <div className='sites-header-container'>
        <div className='sites-header-text'>
          Sites
        </div>
      </div>

      <div className='approved-sites-container'>       
        {loading && <div className='loading'>Loading...</div>}

        {/* Business Selector or Single Business Display */}
        {!loading && uniqueBusinesses.length > 0 && (
          <div className="business-selector-container">
            {uniqueBusinesses.length === 1 ? (
              <div className="single-business-display">
                <span className="business-label">Business:</span>
                <span className="business-name">{uniqueBusinesses[0]}</span>
              </div>
            ) : (
              <Select
                className="business-select-dropdown"
                label="Filter by business"
                value={selectedBusiness}
                onChange={handleBusinessChange}
                options={businessOptions}
               
              />
            )}
          </div>
        )}

        {/* Search Box */}
       <div className='search-container-wrapper'>
         <div className="search-container">         
          <Input
            className="search-box"
            value={searchItem}
            onChange={handleSearchItem}
            placeholder="Search site name ..."
          />
          {searchItem && (
            <span
              className="clear-icon"
              onClick={() => handleClearSearch()}
            >
              &times;
            </span>
          )}
        </div>
       </div>
       
        <div className='table-container-sites'>
          <Table
            columns = {sites_table_columns}
            data = {sites_table_data}
            tableWrapperStyle={{maxHeight: '500px'}} 
          />
        </div>

        {/* Modals */}
        <Modal size='md' show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>
              Existing timezone: 
                <span style={{ color: '#ccc', marginLeft: '0.5em' }}
                  className='tz-edit-modal-header-value'>
                  {selectedRow.local_timezone}
                </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <EditTzModal 
              hwid={selectedRow.hwid}
              closeModal={handleClose}
              site={selectedRow.site}
              setTableData={setTableData}
              tableData={tableData}
            />
          </Modal.Body>
        </Modal>

        <Modal size='md' show={showSI} onHide={handleCloseSI}>
          <Modal.Header closeButton>
            <Modal.Title>
              Existing SI name: 
                <span style={{ color: '#ccc', marginLeft: '0.5em' }}
                  className='tz-edit-modal-header-value'>
                  {selectedRow.si}
                </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <EditSIModal
              hwid={selectedRow.hwid}
              closeModal={handleCloseSI}
              site={selectedRow.site}
              setTableData={setTableData}
              tableData={tableData}
            />
          </Modal.Body>
        </Modal>

        <Modal size='md' show={showSite} onHide={handleCloseSite}>
          <Modal.Header closeButton>
            <Modal.Title>
              Existing Site name:
                <span style={{ color: '#ccc', marginLeft: '0.5em' }}
                  className='tz-edit-modal-header-value'>
                  {selectedRow.site}
                </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <EditSiteModal
              hwid={selectedRow.hwid}
              closeModal={handleCloseSite}
              business={selectedRow.business}
              setTableData={setTableData}
              tableData={tableData}
            />
          </Modal.Body>
        </Modal>

        <Modal size='md' show={showLAT} onHide={handleCloseLAT}>
          <Modal.Header closeButton>
            <Modal.Title>
              Existing Latitude:
                <span style={{ color: '#ccc', marginLeft: '0.5em' }}
                  className='tz-edit-modal-header-value'>
                  {selectedRow.latitude}
                </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <EditLATModal 
              hwid={selectedRow.hwid}
              closeModal={handleCloseLAT} 
              site={selectedRow.site}
              setTableData={setTableData}
              tableData={tableData}
            />
          </Modal.Body>
        </Modal>

        <Modal size='md' show={showLNG} onHide={handleCloseLNG}>
          <Modal.Header closeButton>
            <Modal.Title>
              Existing Longitude:
                <span style={{ color: '#ccc', marginLeft: '0.5em' }}
                  className='tz-edit-modal-header-value'>
                  {selectedRow.longitude}
                </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <EditLNGModal 
              hwid={selectedRow.hwid}
              closeModal={handleCloseLNG} 
              site={selectedRow.site}
              setTableData={setTableData}
              tableData={tableData}
            />
          </Modal.Body>
        </Modal>
      </div>
      
      <div className='superAdmin-footer-container' style={{ padding: '2em 0' }}>
        <Footer url={webURL}/>
      </div>
    </>
  );
}

export default ApprovedSites;